import { use, useState } from "react";
import { useDbClient, useLiveQuery } from "@tanstack/react-db";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import {
  collectionError,
  collectionRefreshing,
  QueryViewerContext,
  refreshCollections,
} from "@softmax/web-features/data";
import { myPlayersCollection } from "@/features/players/records";
import { SweepButton } from "@softmax/design-system";
import { buildHref } from "@softmax/web-addresses";
import { NavigationReady } from "@softmax/web-features/navigation/markers";
import { ProfileSettings } from "./profile-settings";
import { ProfileCredentials } from "../credentials/profile-credentials";
import { AccountBackLink } from "./account-back-link";

import type { AccountPageData } from "../account.types";

export function ProfileAccountContent({
  data,
}: {
  data: Extract<AccountPageData, { kind: "profile" }>;
}) {
  const viewerId = use(QueryViewerContext);
  const db = useDbClient();
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const descriptor = myPlayersCollection({
    viewerId,
    elevatedPrivileges: false,
  });
  const source = db.collection(descriptor);
  const players = useLiveQuery((q) => q.from({ player: descriptor }));
  const error = collectionError(players.health);
  const unavailable = requestUnavailable(error);
  const [refreshing, setRefreshing] = useState(false);
  const busy = refreshing || collectionRefreshing(players.health);
  const refresh = async () => {
    setRefreshing(true);
    await refreshCollections({
      queries: [players.collection],
      refresh: () => source.utils.refetch({ throwOnError: true }),
      signal,
    }).finally(() => setRefreshing(false));
  };

  if (unavailable || !players.isReady) {
    return (
      <div
        role="alert"
        className="mb-[24px] flex flex-wrap items-center gap-[12px]"
      >
        <p>
          {error
            ? `Your agents are unavailable. ${error.message}`
            : "Loading your agents…"}
        </p>
        <SweepButton
          quiet
          type="button"
          disabled={busy || players.isLoading}
          onClick={() => void refresh()}
        >
          Retry account
        </SweepButton>
      </div>
    );
  }

  return (
    <div className="w-full">
      <NavigationReady
        stage="content"
        path={buildHref({ name: "account" })}
        hydrated
      />
      <div className="mx-auto max-w-[1200px] px-[16px] pb-[64px] md:px-[48px] lg:px-[64px] lg:[[data-rail]_&]:px-[32px]!">
        <header className="flex flex-col gap-[12px] pb-[48px]">
          <div className="flex items-center justify-between gap-[16px]">
            <AccountBackLink href={buildHref({ name: "home" })} remember>
              Back
            </AccountBackLink>
          </div>
          <h1 className="home-font-title font-plex-mono text-[28px] leading-[1.11] font-semibold tracking-[-1px] text-[#161616] md:text-[32px]">
            Account settings
          </h1>
          <p className="font-plex-sans text-[18px] leading-[28.8px] text-[#5e5e5e]">
            Manage your profile, your agents, communication preferences, linked
            accounts, experience credits, and access tokens.
          </p>
        </header>
        <ProfileSettings {...data.settings} agents={players.data} />
        {error ? (
          <output className="mb-[24px] flex flex-wrap items-center gap-[12px] font-plex-sans text-[16px] leading-[24px] text-[#945637]">
            <span>Saved agents could not be refreshed: {error.message}</span>
            <SweepButton
              quiet
              type="button"
              disabled={busy}
              onClick={() => void refresh()}
            >
              Refresh agents
            </SweepButton>
          </output>
        ) : null}
        <ProfileCredentials agents={players.data} />
      </div>
    </div>
  );
}
